/**
 * Asking for notification permission, at the moment it makes sense.
 *
 * Without this, the only way to turn notifications on is to go looking for
 * Settings — which nobody does, so students simply never get push. Admin can
 * decide who *should* be notified, but only the person holding the phone can
 * grant it, and they have to be asked.
 *
 * Rules it follows:
 *
 *   • The tap on the button *is* the permission request. Browsers ignore a
 *     prompt that no gesture asked for, and Safari holds that activation for
 *     only a moment — see the note in lib/push.ts.
 *
 *   • It never appears when it cannot work. In a Safari tab on iPhone the Push
 *     API does not exist, so this points at installing instead of offering a
 *     button that would do nothing.
 *
 *   • A refusal is respected. "Not now" hides it for the session; "Don't ask
 *     again" is permanent and stored on the account. It never reappears after
 *     a browser-level denial, because at that point only iOS Settings can undo
 *     it and the app pretending otherwise would be a lie.
 */

import { useEffect, useState } from 'react';
import { Bell, Share, SquarePlus, X } from 'lucide-react';

import { useSession } from '../../state/SessionProvider';
import { currentPushState, enablePush, type PushState } from '../../lib/push';
import { useToast } from '../../state/ToastProvider';
import { isIOS, isStandalone } from '../../lib/serviceWorker';

const SNOOZE_KEY = 'ss.push.snoozed';

export function PushInvite() {
  const { status, prefs, savePrefs, effectiveRole } = useSession();
  const toast = useToast();

  const [pushState, setPushState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [snoozed, setSnoozed] = useState(() => {
    try {
      return sessionStorage.getItem(SNOOZE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (status !== 'ready') return;
    void currentPushState().then(setPushState);
  }, [status]);

  if (status !== 'ready' || snoozed) return null;
  if (prefs.dismissedPushPrompt) return null;

  // Nothing useful to say in these states: already on, permanently blocked, or
  // a browser that has no push at all.
  if (pushState !== 'prompt' && pushState !== 'granted-not-subscribed' && pushState !== 'needs-install') {
    return null;
  }

  /*
   * On an iPhone in Safari, installing is the prerequisite — and the install
   * coaching already covers that. Two overlapping prompts about the same thing
   * is worse than one.
   */
  if (pushState === 'needs-install') {
    if (!isIOS() || isStandalone()) return null;
    return (
      <div className="pushinvite" role="region" aria-label="Notifications">
        <span className="pushinvite__icon" aria-hidden="true">
          <Bell size={18} />
        </span>
        <div className="pushinvite__body">
          <p className="pushinvite__title">Get notified about new posts</p>
          <p className="pushinvite__text">
            On iPhone, notifications need SamuelSchool on your Home Screen first. Tap{' '}
            <Share size={13} aria-label="Share" style={{ verticalAlign: '-2px' }} /> Share, then{' '}
            <SquarePlus size={13} aria-hidden="true" style={{ verticalAlign: '-2px' }} /> Add to Home
            Screen, and open it from there.
          </p>
        </div>
        <button
          type="button"
          className="icon-btn pushinvite__close"
          aria-label="Dismiss"
          onClick={() => snooze(setSnoozed)}
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="pushinvite" role="region" aria-label="Notifications">
      <span className="pushinvite__icon" aria-hidden="true">
        <Bell size={18} />
      </span>

      <div className="pushinvite__body">
        <p className="pushinvite__title">Turn on notifications</p>
        <p className="pushinvite__text">
          {effectiveRole === 'admin'
            ? 'Get announcements and updates on this device, even when the app is closed.'
            : 'Know when Samuel posts an announcement or adds a file — even when the app is closed.'}
        </p>

        <div className="row pushinvite__actions">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy}
            // No await before enablePush(): the permission request has to
            // happen inside this gesture or Safari discards it.
            onClick={async () => {
              setBusy(true);
              try {
                const next = await enablePush();
                setPushState(next);
                if (next === 'subscribed') {
                  toast.show('Notifications are on for this device');
                } else if (next === 'denied') {
                  toast.error('Your browser blocked notifications. Turn them on in Settings to undo.');
                }
              } catch (err) {
                toast.error((err as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <span className="spinner" /> : <Bell size={15} />}
            Turn on
          </button>

          <button type="button" className="btn btn--ghost btn--sm" onClick={() => snooze(setSnoozed)}>
            Not now
          </button>

          <button
            type="button"
            className="btn btn--ghost btn--sm faint"
            onClick={() => void savePrefs({ dismissedPushPrompt: true })}
          >
            Don&rsquo;t ask again
          </button>
        </div>
      </div>
    </div>
  );
}

function snooze(setSnoozed: (value: boolean) => void): void {
  setSnoozed(true);
  try {
    sessionStorage.setItem(SNOOZE_KEY, '1');
  } catch {
    /* in-memory for this view is enough */
  }
}
